import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import request from "@/lib/api/handler";
import { toast } from "sonner";
import { CompanyJobOpening, DraftJobOpeningDescription } from "@/types";
import { QueryKeys, useInvalidateQuery } from "@/queries/utils";
import { CompanyOpportunityFormSchema } from "./schema";
import localDateToDateString from "@/utils/localDateToDateString";

type NdjsonEvent =
  | { type: "begin"; requestId?: string }
  | { type: "token"; token: string }
  | { type: "done"; draft: DraftJobOpeningDescription; requestId?: string }
  | { type: "error"; code: string; message: string; requestId?: string };

interface AIDescriptionProps {
  openingId: string;
  description?: string;
  message?: string;
  onToken?: (token: string, full: string) => void;
  onSuccess?: (draft: DraftJobOpeningDescription) => void;
  onError?: () => void;
}

export const useCompanyOpportunityDraftMutation = (
  id?: string,
  companyId?: string,
) => {
  const qc = useQueryClient();
  const invalidate = useInvalidateQuery();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async ({
      data,
      mode,
    }: {
      data: CompanyOpportunityFormSchema;
      mode: "create" | "update" | "publish";
      onSuccess?: (data: CompanyJobOpening) => void;
    }) => {
      if (mode !== "create" && !id) {
        throw new Error("No ID provided for update operation");
      }

      const token = await getAccessTokenSilently();
      const body = buildFormData(data, mode);

      if (
        mode !== "create" &&
        data.jobDescription?.name === "" &&
        data.jobDescription.size === 0
      ) {
        body.delete("jobDescription");
      }

      const response: CompanyJobOpening = await request({
        method: mode === "create" ? "POST" : "PATCH",
        token,
        path: mode === "create" ? `/api/jobOpening/` : `/api/jobOpening/${id}`,
        params:
          mode === "create"
            ? {
                companyId,
                draft: true,
              }
            : mode === "publish"
              ? {
                  publish: true,
                }
              : {},
        body,
        contentType: "application/x-www-form-urlencoded",
      });

      return response;
    },
    async onSuccess(data, variables) {
      if (variables.mode !== "create") {
        toast.success(
          `Opportunity ${variables.mode === "publish" ? "created" : "updated"} successfully!`,
        );
      }

      variables.onSuccess?.(data);

      qc.setQueryData(QueryKeys.companyOpportunity(data.id), data);

      await invalidate(QueryKeys.companyOpportunities);
      await invalidate(QueryKeys.adminJobDetails(data.id));

      if (variables.mode === "publish") {
        await invalidate(QueryKeys.companyDraftJobDescriptions(data.id));
      }
    },
  });
};

function buildFormData(
  data: CompanyOpportunityFormSchema,
  mode: "create" | "update" | "publish",
) {
  const body = new FormData();

  if (mode === "create") {
    body.append("type", "Full-time");
  }

  body.append("role", data.jobTitle);
  body.append("budgetMinCents", data.budgetMinCents.toString());
  body.append("budgetMaxCents", data.budgetMaxCents.toString());
  body.append("budgetNotes", data.budgetNotes ?? "");
  body.append("desiredStartDate", localDateToDateString(data.idealStartDate));
  body.append("shortDescription", data.briefDescription);
  body.append("goals", data.goals);
  body.append("matchCriteria1", data.matchCriteria[0] ?? "");
  body.append("matchCriteria2", data.matchCriteria[1] ?? "");
  body.append("matchCriteria3", data.matchCriteria[2] ?? "");
  body.append("noteForVenturous", data.additionalInformation ?? "");

  if (data.jobDescription) {
    body.append("jobDescription", data.jobDescription);
  }

  if (data.maskedJobDescription) {
    body.append("maskedJobDescription", data.maskedJobDescription);
  }

  return body;
}

export const useCompanyOpportunityAIDescriptionMutation = () => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (data: AIDescriptionProps) => {
      const token = await getAccessTokenSilently();

      const startResp = await fetch(
        `/api/jobOpening/${data.openingId}/generate_job_description_stream/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            description: data.description ?? "",
            message: data.message ?? "",
          }),
        },
      );

      if (!startResp.ok) {
        throw new Error(`Start failed: ${startResp.status}`);
      }

      const { streamId } = (await startResp.json()) as { streamId: string };

      let full = "";
      let finalDraft: DraftJobOpeningDescription | null = null;

      await readNdjsonStream({
        url: `/api/jobOpening/generate_job_description_stream/${encodeURIComponent(streamId)}/connect`,
        token,
        onToken: (t) => {
          full += t;
          data.onToken?.(token, full);
        },
        onDone: (draft) => {
          finalDraft = draft;
        },
        onError: (e) => {
          throw new Error(e.message);
        },
      });

      if (!finalDraft) throw new Error("No draft returned");

      return finalDraft as DraftJobOpeningDescription;
    },
    onSuccess(data, variables) {
      variables.onSuccess?.(data);
      qc.setQueryData<DraftJobOpeningDescription[]>(
        QueryKeys.companyDraftJobDescriptions(variables.openingId),
        (old) => {
          if (!old) {
            return [data];
          }

          return [...old, data];
        },
      );
    },
    onError(_, variables) {
      variables.onError?.();
    },
  });
};

async function readNdjsonStream(opts: {
  url: string;
  token: string;
  onToken: (t: string) => void;
  onBegin?: (e: Extract<NdjsonEvent, { type: "begin" }>) => void;
  onDone: (draft: DraftJobOpeningDescription) => void;
  onError: (e: Extract<NdjsonEvent, { type: "error" }>) => void;
  signal?: AbortSignal;
}) {
  const resp = await fetch(opts.url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${opts.token}`,
    },
    signal: opts.signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Connect failed: ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
    while (true) {
      const nl = buffer.indexOf("\n");
      if (nl === -1) break;

      const rawLine = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);

      const line = rawLine.replace(/\r$/, "").trim();
      if (!line) continue;

      let evt: NdjsonEvent;
      try {
        evt = JSON.parse(line) as NdjsonEvent;
      } catch {
        continue;
      }

      if (evt.type === "begin") opts.onBegin?.(evt);
      if (evt.type === "token") opts.onToken(evt.token);
      if (evt.type === "error") {
        opts.onError(evt);
        return; // stop processing
      }
      if (evt.type === "done") {
        opts.onDone(evt.draft);
        return; // stop processing
      }
    }
  }

  // If we exit without done/error, treat as interrupted.
  throw new Error("Stream ended unexpectedly (no done/error event).");
}
